import { useState, useEffect, useRef, useCallback } from 'react';

const WS_RECONNECT_DELAY = 2000;
const WS_MAX_RECONNECT = 10;

export function useWebSocket(sessionId, onMessage) {
  const [isConnected, setIsConnected] = useState(false);
  const [jarvisState, setJarvisState] = useState('BOOTING');
  const wsRef = useRef(null);
  const reconnectCount = useRef(0);
  const reconnectTimer = useRef(null);
  const mountedRef = useRef(true);
  const connectingRef = useRef(false);
  const onMessageRef = useRef(onMessage);

  // Keep callback ref current
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const getWsUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = backendUrl.replace(/^http/, 'ws');
    return `${wsBase}/api/ws/${sessionId}`;
  }, [sessionId]);

  const connect = useCallback(() => {
    if (connectingRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) return;
    if (!mountedRef.current) return;

    connectingRef.current = true;

    try {
      const url = getWsUrl();
      console.log('[WS] Connecting to:', url);
      const ws = new WebSocket(url);

      ws.onopen = () => {
        if (!mountedRef.current) {
          ws.close();
          return;
        }
        console.log('[WS] Connected');
        setIsConnected(true);
        reconnectCount.current = 0;
        connectingRef.current = false;
        wsRef.current = ws;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const data = JSON.parse(event.data);
          console.log('[WS] Received:', data.type);

          // Handle state changes internally
          if (data.type === 'connected') {
            setJarvisState('ARMED');
          } else if (data.type === 'state_change') {
            setJarvisState(data.state);
          }

          // Forward ALL messages to the handler directly (no state batching)
          if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (e) {
          console.error('[WS] Parse error:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('[WS] Disconnected, code:', event.code);
        connectingRef.current = false;
        
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
        
        if (!mountedRef.current) return;
        
        setIsConnected(false);

        if (reconnectCount.current < WS_MAX_RECONNECT && mountedRef.current) {
          reconnectCount.current++;
          console.log(`[WS] Reconnecting (${reconnectCount.current}/${WS_MAX_RECONNECT})...`);
          reconnectTimer.current = setTimeout(() => {
            if (mountedRef.current) connect();
          }, WS_RECONNECT_DELAY);
        }
      };

      ws.onerror = () => {
        console.error('[WS] Error');
        connectingRef.current = false;
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('[WS] Connection error:', e);
      connectingRef.current = false;
    }
  }, [getWsUrl]);

  const sendMessage = useCallback((data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      console.log('[WS] Sending:', data.type || data.command);
      ws.send(JSON.stringify(data));
      return true;
    }
    console.warn('[WS] Cannot send - not connected');
    return false;
  }, []);

  const sendVoiceInput = useCallback((text, mode = 'quick', muted = false) => {
    return sendMessage({
      type: 'voice_input',
      text,
      mode,
      muted
    });
  }, [sendMessage]);

  const sendCommand = useCallback((command) => {
    return sendMessage({
      type: 'command',
      command
    });
  }, [sendMessage]);

  useEffect(() => {
    mountedRef.current = true;
    connectingRef.current = false;
    
    const timer = setTimeout(() => {
      if (mountedRef.current) connect();
    }, 100);
    
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      clearTimeout(reconnectTimer.current);
      connectingRef.current = false;
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
      }
    };
  }, [connect]);

  return {
    isConnected,
    jarvisState,
    setJarvisState,
    sendMessage,
    sendVoiceInput,
    sendCommand,
    reconnect: connect
  };
}
